// Swipe-down-to-dismiss for modal bottom sheets on mobile.
//
// Standard modals render as bottom sheets below the `sm` breakpoint. This lets
// the user drag the sheet down and, past a threshold, dismiss it. Dismissal
// reuses the sheet's existing `.modal__backdrop-dismiss` control (wired to the
// modal's `onClose`), so every bottom sheet gets the gesture with no per-modal
// Elm wiring.

const MOBILE_QUERY = "(max-width: 767px)";
const CLOSE_THRESHOLD_PX = 100;
const CLOSE_TRANSITION = "transform 0.2s ease-out";

const BottomSheetSwipePort = {
  add: function () {
    let sheet = null;
    let scrollers = [];
    let startY = null;
    let delta = 0;
    let dragging = false;

    const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;

    const sheetFor = (target) => (target && target.closest ? target.closest(".modal") : null);

    const isScrollable = (el) => {
      const overflowY = window.getComputedStyle(el).overflowY;
      return (overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight;
    };

    // A drag down only dismisses once every scroller under the finger is at its
    // top. composedPath() includes shadow roots such as <pdf-document>'s.
    const scrollersFor = (event, target) => {
      const path = event.composedPath ? event.composedPath() : [];
      const inner = [];
      for (const node of path) {
        if (node === target) break;
        if (node instanceof Element && isScrollable(node)) inner.push(node);
      }
      const body = target.querySelector(".modal__body");
      return body && !inner.includes(body) ? inner.concat(body) : inner;
    };

    const resetSheetStyles = (transition) => {
      if (!sheet) return;
      sheet.style.transition = transition || "";
      sheet.style.transform = "";
    };

    const clearState = () => {
      sheet = null;
      scrollers = [];
      startY = null;
      delta = 0;
      dragging = false;
    };

    const onTouchStart = (event) => {
      if (!isMobile() || event.touches.length !== 1) return;
      const target = sheetFor(event.target);
      if (!target) return;
      sheet = target;
      scrollers = scrollersFor(event, target);
      startY = event.touches[0].clientY;
      delta = 0;
      dragging = false;
    };

    const onTouchMove = (event) => {
      if (startY === null || !sheet) return;
      delta = event.touches[0].clientY - startY;
      const scrollersAtTop = scrollers.every((el) => el.scrollTop <= 0);

      if (delta > 0 && scrollersAtTop) {
        dragging = true;
        sheet.style.transition = "none";
        sheet.style.transform = "translateY(" + delta + "px)";
        // Prevent the page/body from scrolling while dragging the sheet.
        event.preventDefault();
      } else if (dragging) {
        // Dragged back above the start point: pin to the top edge.
        sheet.style.transform = "translateY(0px)";
      }
    };

    const dismiss = () => {
      const wrap = sheet.closest(".modal-wrap");
      const dismissControl = wrap ? wrap.querySelector(".modal__backdrop-dismiss") : null;
      const target = sheet;
      target.style.transition = CLOSE_TRANSITION;
      target.style.transform = "translateY(100%)";

      let finished = false;
      let fallbackTimer = null;
      const finish = () => {
        if (finished) return;
        finished = true;
        clearTimeout(fallbackTimer);
        target.removeEventListener("transitionend", finish);
        if (dismissControl) dismissControl.click();
      };
      target.addEventListener("transitionend", finish);
      // Fallback in case transitionend doesn't fire (e.g. reduced motion).
      fallbackTimer = setTimeout(finish, 250);
      clearState();
    };

    const onTouchEnd = () => {
      if (startY === null || !sheet) {
        clearState();
        return;
      }
      if (dragging && delta > CLOSE_THRESHOLD_PX) {
        dismiss();
      } else {
        resetSheetStyles(CLOSE_TRANSITION);
        clearState();
      }
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    document.addEventListener(
      "touchcancel",
      () => {
        resetSheetStyles(CLOSE_TRANSITION);
        clearState();
      },
      { passive: true },
    );
  },
};

module.exports = BottomSheetSwipePort;
